/**
 * GuidancePage (`/guidance`) — Bayenah V3 legal-guidance hub.
 *
 * Three tabs (no "all") with a black underline on the active tab:
 *   - نظام العمل
 *   - نظام الشركات
 *   - العقود والمعاملات المدنية
 *
 * Each card carries: topic title, brief description, and a law reference
 * preceded by a black dot. Clicking "اسأل بيّنة ←" seeds a guidance
 * question into the assistant.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';
import { TERMS } from '@/lib/terminology';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { id: 'labor',      ar: 'نظام العمل',                 en: 'Labor Law' },
  { id: 'companies',  ar: 'نظام الشركات',               en: 'Companies Law' },
  { id: 'civil',      ar: 'العقود والمعاملات المدنية',  en: 'Civil Transactions' },
];

const GUIDES = [
  // Labor — 3 cards
  { id: 'labor-probation',   category: 'labor',     ar_title: 'فترة التجربة',                en_title: 'Probation period',           ar_desc: 'مدة فترة التجربة، شروط الفصل خلالها، وحقوق الموظف.', en_desc: 'Probation duration, termination during probation, employee rights.', ref_ar: 'نظام العمل — المادة ٥٣',  ref_en: 'Labor Law — Art. 53',  question: 'اشرح أحكام فترة التجربة وفق نظام العمل السعودي.' },
  { id: 'labor-eos',         category: 'labor',     ar_title: 'مكافأة نهاية الخدمة',         en_title: 'End-of-service reward',      ar_desc: 'احتساب مكافأة نهاية الخدمة وفق سنوات الخدمة.',         en_desc: 'How to calculate end-of-service reward by years of service.',         ref_ar: 'نظام العمل — المادة ٨٤',  ref_en: 'Labor Law — Art. 84',  question: 'كيف تُحتسب مكافأة نهاية الخدمة وفق نظام العمل؟' },
  { id: 'labor-hours',       category: 'labor',     ar_title: 'ساعات العمل والإضافي',         en_title: 'Working hours & overtime',   ar_desc: 'الحد الأقصى للساعات الأسبوعية وأحكام الأجر الإضافي.',  en_desc: 'Weekly hour cap and overtime pay rules.',                              ref_ar: 'نظام العمل — المواد ٩٨–١٠٧', ref_en: 'Labor Law — Arts. 98–107', question: 'ما عدد ساعات العمل القصوى وأحكام الأجر الإضافي؟' },

  // Companies — 3 cards
  { id: 'companies-llc',     category: 'companies', ar_title: 'تأسيس شركة ذات مسؤولية محدودة', en_title: 'Establishing an LLC',        ar_desc: 'الحد الأدنى للشركاء، رأس المال، وخطوات التأسيس.',     en_desc: 'Minimum partners, capital, and incorporation steps.',                  ref_ar: 'نظام الشركات — الفصل الرابع', ref_en: 'Companies Law — Ch. 4',  question: 'ما متطلبات تأسيس شركة ذات مسؤولية محدودة؟' },
  { id: 'companies-jsc',     category: 'companies', ar_title: 'الشركة المساهمة',              en_title: 'Joint-stock company',        ar_desc: 'هيكل الحوكمة، اجتماعات الجمعية، وحقوق المساهمين.',     en_desc: 'Governance structure, GA meetings, and shareholder rights.',          ref_ar: 'نظام الشركات — الفصل الخامس', ref_en: 'Companies Law — Ch. 5',  question: 'ما الفرق بين الشركة المساهمة والشركة ذات المسؤولية المحدودة؟' },
  { id: 'companies-board',   category: 'companies', ar_title: 'مجلس الإدارة وصلاحياته',       en_title: 'Board of directors',         ar_desc: 'تشكيل المجلس، الصلاحيات، واتخاذ القرارات في الشركة المساهمة.', en_desc: 'Board composition, powers, and decision-making in JSCs.',         ref_ar: 'نظام الشركات — المواد ٦٨–٩٢', ref_en: 'Companies Law — Arts. 68–92', question: 'ما صلاحيات مجلس الإدارة وفق نظام الشركات السعودي؟' },

  // Civil — 3 cards
  { id: 'civil-formation',   category: 'civil',     ar_title: 'تكوين العقد',                 en_title: 'Contract formation',         ar_desc: 'أركان العقد الصحيح: الإيجاب، القبول، المحل والسبب.',  en_desc: 'Elements of a valid contract: offer, acceptance, subject, cause.',     ref_ar: 'نظام المعاملات المدنية — المواد ٣٤–٤٧', ref_en: 'Civil Transactions Law — Arts. 34–47', question: 'ما أركان تكوين العقد وفق نظام المعاملات المدنية؟' },
  { id: 'civil-termination', category: 'civil',     ar_title: 'فسخ العقد',                   en_title: 'Contract termination',       ar_desc: 'أسباب الفسخ المشروع وآثاره القانونية.',               en_desc: 'Lawful termination grounds and their legal effects.',                  ref_ar: 'نظام المعاملات المدنية — المواد ١٠٧–١١٢', ref_en: 'Civil Transactions Law — Arts. 107–112', question: 'متى يجوز فسخ العقد وما هي آثار الفسخ؟' },
  { id: 'civil-damages',     category: 'civil',     ar_title: 'التعويض عن الإخلال',          en_title: 'Damages for breach',         ar_desc: 'احتساب التعويض، الشرط الجزائي، وحدود المسؤولية المدنية.', en_desc: 'Calculating damages, penalty clauses, and civil-liability caps.', ref_ar: 'نظام المعاملات المدنية — المواد ١٣٢–١٣٧', ref_en: 'Civil Transactions Law — Arts. 132–137', question: 'كيف يُحتسب التعويض عن الإخلال بالعقد وفق نظام المعاملات المدنية؟' },
];

export default function GuidancePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [active, setActive] = useState('labor');

  const visible = useMemo(() => GUIDES.filter((g) => g.category === active), [active]);

  const handleAsk = (g) => {
    const q = isArabic ? g.question : `Explain: ${g.en_title}`;
    navigate(`/chat?prompt=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-full w-full" data-testid="guidance-page" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="w-5 h-5 text-[#111111]" strokeWidth={1.7} />
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#111111] font-arabic">
            {isArabic ? TERMS.guidance : 'Legal guidance'}
          </h1>
        </div>
        <p className="text-[#666666] text-sm max-w-2xl mb-8 font-arabic">
          {isArabic
            ? 'إرشادات وتفسيرات للأنظمة السعودية الرئيسية، مع روابط للمواد النظامية المرتبطة.'
            : 'Guidance and interpretations for the main Saudi laws, with links to the governing articles.'}
        </p>

        {/* Underline tab bar (Test 13.2/13.3) */}
        <div
          className={cn('flex gap-6 mb-8 border-b border-[#E8E8E8]', isArabic && 'flex-row-reverse')}
          data-testid="guidance-filters"
          role="tablist"
        >
          {CATEGORIES.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(c.id)}
                className={cn(
                  'pb-3 text-[14px] transition-colors font-arabic relative',
                  isActive ? 'text-[#111111] font-semibold' : 'text-[#666666] hover:text-[#111111]',
                )}
                data-testid={`guidance-cat-${c.id}`}
              >
                {isArabic ? c.ar : c.en}
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#111111]"
                    data-testid={`guidance-cat-${c.id}-underline`}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="guidance-grid">
          {visible.map((g) => (
            <article
              key={g.id}
              className="bg-white rounded-lg border border-[#E8E8E8] p-5 flex flex-col gap-3 transition-colors hover:border-[#999999]"
              data-testid={`guidance-card-${g.id}`}
            >
              <h2 className={cn('text-[15px] font-medium text-[#111111] font-arabic', isArabic && 'text-right')}>
                {isArabic ? g.ar_title : g.en_title}
              </h2>
              <p className={cn('text-[12.5px] text-[#666666] leading-relaxed font-arabic flex-1', isArabic && 'text-right')}>
                {isArabic ? g.ar_desc : g.en_desc}
              </p>
              {/* Law reference with black dot bullet (Test 13.4) */}
              <div
                className={cn('flex items-center gap-2 font-arabic', isArabic && 'flex-row-reverse')}
                data-testid={`guidance-card-${g.id}-ref`}
              >
                <span
                  aria-hidden="true"
                  className="inline-block bg-[#111111] rounded-full"
                  style={{ width: 6, height: 6 }}
                />
                <span className="text-[11.5px] text-[#666666]">
                  {isArabic ? g.ref_ar : g.ref_en}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleAsk(g)}
                className={cn(
                  'self-start mt-1 inline-flex items-center gap-1 text-[12.5px] font-arabic transition',
                  'text-[#111111] hover:underline',
                  isArabic && 'flex-row-reverse',
                )}
                data-testid={`guidance-ask-${g.id}`}
              >
                <span>{isArabic ? 'اسأل بيّنة' : 'Ask Bayenah'}</span>
                <span aria-hidden="true">{isArabic ? '←' : '→'}</span>
              </button>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
